/**
 * KimiPatternsAdmin — /admin/kimi-patterns
 *
 * 1) Canvas live DualCTA → ANA* → Stripe (poll /api/admin/kimi-live-metrics)
 * 2) Veredicto diario de patterns (GET/POST /api/admin/kimi-patterns)
 *
 * No-fail: live metrics always paint; never block checkout path.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import type {
  KimiLiveConfig,
  KimiLiveMetrics,
} from "@/components/admin/kimi/kimiLiveFlowModel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Activity,
  Brain,
  Check,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Sparkles,
  Tag,
  Zap,
} from "lucide-react";
import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";

const LazyKimiLiveFlowCanvas = lazy(() =>
  import("@/components/admin/kimi/KimiLiveFlowCanvas").then((m) => ({
    default: m.KimiLiveFlowCanvas,
  })),
);

/** Scoped boundary: xyflow/chunk failure must not kill the whole admin SPA. */
class KimiCanvasErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[KimiCanvasErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-[280px] flex-col items-center justify-center gap-3 rounded-2xl border border-amber-400/25 bg-[#070708] px-4 text-center">
          <p className="text-sm font-medium text-amber-100/90">
            Canvas live no disponible
          </p>
          <p className="max-w-md text-xs text-white/45">
            El grafo no cargó (chunk o React Flow). KPIs y veredicto abajo siguen
            operativos — no afecta el checkout público.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="text-xs"
            onClick={() => this.setState({ error: null })}
          >
            Reintentar canvas
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

type DayStats = {
  decisions: number;
  with_promo: number;
  distinct_codes: number;
} | null;

type PatternRow = {
  id: string;
  pattern_key: string;
  title?: string | null;
  status: string;
  confidence: number;
  sample_size: number;
  recommendation?: {
    action?: string;
    target_tier?: string;
    percent_off?: number;
    billing_plan?: string;
    code?: string;
  } | null;
  llm_summary?: string | null;
  verdict_date?: string;
};

type OpportunityRow = {
  id: string;
  kind?: string | null;
  title?: string | null;
  description?: string | null;
  evidence?: unknown;
  priority?: number | null;
  status: string;
  related_pattern_id?: string | null;
  created_by?: string | null;
  created_at?: string;
};

type KimiPatternsResponse = {
  ok: boolean;
  brand_source_key?: string;
  market?: string;
  focus?: string;
  verdict_date: string;
  patterns: PatternRow[];
  day_stats?: DayStats;
  opportunities?: OpportunityRow[];
  llm?: {
    enabled?: boolean;
    key_configured?: boolean;
    model?: string;
    provider?: string;
    path?: string;
  };
  error?: string;
};

type LiveMetricsResponse = {
  ok?: boolean;
  brand_source_key?: string;
  market?: string;
  hours?: number;
  generated_at?: string;
  config?: KimiLiveConfig;
  metrics?: KimiLiveMetrics;
  health?: { ok?: boolean; warnings?: string[] };
  error?: string;
};

const LIVE_POLL_MS = 20_000;
const LIVE_HOURS_OPTIONS = ["1", "6", "24"] as const;

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatTs(v: unknown): string {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 19);
    return d.toLocaleString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      day: "2-digit",
      month: "2-digit",
    });
  } catch {
    return "—";
  }
}

function formatRecommendation(rec?: PatternRow["recommendation"] | null): string {
  if (!rec || typeof rec !== "object") return "—";
  const parts: string[] = [];
  if (rec.action) parts.push(rec.action);
  if (rec.code) parts.push(rec.code);
  if (typeof rec.percent_off === "number") parts.push(`${rec.percent_off}%`);
  if (rec.target_tier) parts.push(`tier:${rec.target_tier}`);
  if (rec.billing_plan) parts.push(rec.billing_plan);
  return parts.length ? parts.join(" · ") : JSON.stringify(rec);
}

function statusBadgeVariant(
  status: string,
): "default" | "secondary" | "outline" | "destructive" {
  const s = status.toLowerCase();
  if (s === "active") return "default";
  if (s === "open") return "secondary";
  if (s === "accepted") return "outline";
  if (s === "rejected" || s === "expired") return "destructive";
  return "outline";
}

export default function KimiPatternsAdmin() {
  const { getAuthHeaders, isLoaded, isSignedIn } = useAdminAuth();
  const { toast } = useToast();

  const [date, setDate] = useState(todayUtc);
  const [loading, setLoading] = useState(true);
  const [aggregating, setAggregating] = useState(false);
  const [llmRunning, setLlmRunning] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [data, setData] = useState<KimiPatternsResponse | null>(null);

  // Live canvas state
  const [liveHours, setLiveHours] = useState<string>("6");
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveSilent, setLiveSilent] = useState(false);
  const [live, setLive] = useState<LiveMetricsResponse | null>(null);
  const [liveUpdatedAt, setLiveUpdatedAt] = useState<number | null>(null);
  const [liveTick, setLiveTick] = useState(0);

  const loadLive = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!isLoaded || !isSignedIn) return;
      if (!opts?.silent) setLiveLoading(true);
      else setLiveSilent(true);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(
          `/api/admin/kimi-live-metrics?hours=${encodeURIComponent(liveHours)}`,
          { headers },
        );
        const json = (await res.json().catch(() => ({}))) as LiveMetricsResponse;
        // API is no-fail 200; still accept partial payloads
        if (res.status === 401) {
          if (!opts?.silent) {
            toast({
              title: "Live metrics: no autorizado",
              description: "Reingresá al admin.",
              variant: "destructive",
            });
          }
          return;
        }
        setLive(json);
        setLiveUpdatedAt(Date.now());
      } catch (e) {
        // Never hard-fail the page — canvas shows idle zeros
        if (!opts?.silent) {
          toast({
            title: "Live metrics temporalmente no disponible",
            description: e instanceof Error ? e.message : String(e),
            variant: "destructive",
          });
        }
      } finally {
        setLiveLoading(false);
        setLiveSilent(false);
      }
    },
    [getAuthHeaders, isLoaded, isSignedIn, liveHours, toast],
  );

  const load = useCallback(async () => {
    if (!isLoaded) return;
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        date,
        include: "all",
      });
      const res = await fetch(`/api/admin/kimi-patterns?${params}`, { headers });
      const json = (await res.json().catch(() => ({}))) as KimiPatternsResponse;
      if (!res.ok) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      setData(json);
    } catch (e) {
      toast({
        title: "No se pudo cargar el veredicto",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [date, getAuthHeaders, isLoaded, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadLive();
  }, [loadLive]);

  // Real-time poll while tab visible (Delfi pattern ~20–25s)
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let timer: number | null = null;

    const tick = () => {
      if (document.visibilityState === "visible") {
        void loadLive({ silent: true });
      }
    };

    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };

    timer = window.setInterval(tick, LIVE_POLL_MS);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (timer) window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isLoaded, isSignedIn, loadLive]);

  // Refresh "hace Xs" label every second without re-fetch
  useEffect(() => {
    const t = window.setInterval(() => setLiveTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  const liveUpdatedLabel = useMemo(() => {
    void liveTick;
    if (!liveUpdatedAt) return liveSilent ? "Actualizando…" : liveLoading ? "Cargando…" : undefined;
    const sec = Math.max(0, Math.round((Date.now() - liveUpdatedAt) / 1000));
    return liveSilent
      ? `Actualizando… · último ok hace ${sec}s`
      : `Actualizado hace ${sec}s · auto-refresh ${LIVE_POLL_MS / 1000}s`;
  }, [liveUpdatedAt, liveSilent, liveLoading, liveTick]);

  const liveConfig = live?.config ?? null;
  const liveMetrics: KimiLiveMetrics | null = live?.metrics
    ? {
        ...live.metrics,
        hours: live.hours ?? (Number(liveHours) || 6),
      }
    : null;
  const liveWarnings = live?.health?.warnings ?? [];
  const recentDecisions = (liveMetrics?.recent ?? []) as Array<Record<string, unknown>>;

  const runAggregate = async () => {
    setAggregating(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/kimi-patterns", {
        method: "POST",
        headers,
        body: JSON.stringify({ action: "aggregate", date }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        [k: string]: unknown;
      };
      if (!res.ok || json.ok === false) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      toast({
        title: "Aggregate ejecutado",
        description: `Veredicto recalculado para ${date} (UTC).`,
      });
      await load();
    } catch (e) {
      toast({
        title: "Aggregate falló",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setAggregating(false);
    }
  };

  const runLlmInsight = async () => {
    setLlmRunning(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/kimi-patterns", {
        method: "POST",
        headers,
        body: JSON.stringify({ action: "llm_insight", date }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        skipped?: boolean;
        reason?: string;
        error?: string;
        insight_id?: string;
        model?: string;
      };
      if (!res.ok || json.ok === false) {
        throw new Error(json.error || json.reason || `HTTP ${res.status}`);
      }
      if (json.skipped) {
        toast({
          title: "LLM insight omitido",
          description: json.reason || "Key/modelo no configurados",
        });
      } else {
        toast({
          title: "Insight Kimi generado",
          description: `model=${json.model || "?"} id=${json.insight_id || "—"}`,
        });
      }
      await load();
    } catch (e) {
      toast({
        title: "LLM insight falló",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setLlmRunning(false);
    }
  };

  const acceptOpportunity = async (opportunityId: string) => {
    setAcceptingId(opportunityId);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/kimi-patterns", {
        method: "POST",
        headers,
        body: JSON.stringify({
          action: "accept_opportunity",
          opportunity_id: opportunityId,
        }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        updated?: number;
        error?: string;
      };
      if (!res.ok || json.ok === false) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      if (!json.updated) {
        toast({
          title: "Sin cambios",
          description: "La oportunidad ya no está open o no existe.",
        });
      } else {
        toast({ title: "Oportunidad aceptada" });
      }
      await load();
    } catch (e) {
      toast({
        title: "No se pudo aceptar",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setAcceptingId(null);
    }
  };

  const stats = data?.day_stats;
  const patterns = data?.patterns ?? [];
  const openOpportunities = useMemo(
    () => (data?.opportunities ?? []).filter((o) => o.status === "open"),
    [data?.opportunities],
  );
  const otherOpportunities = useMemo(
    () => (data?.opportunities ?? []).filter((o) => o.status !== "open"),
    [data?.opportunities],
  );

  const kimiOn = liveConfig?.kimi_enabled !== false;
  const llmKeyOk = Boolean(liveConfig?.llm_key);
  const resolvesN = liveMetrics?.resolves ?? 0;
  const paidN = liveMetrics?.outcomes?.paid ?? 0;
  const openN = liveMetrics?.outcomes?.checkout_opened ?? 0;

  return (
    <AdminPageShell width="full" className="space-y-5">
      <AdminPageHeader
        title="Offer engine"
        description={
          <>
            <strong className="font-medium text-white/85">
              Pipeline live DualCTA → cupones ANA* → Stripe
            </strong>
            {" · "}
            mercado internacional. El grafo observa volumen real; no modifica el
            checkout público.
          </>
        }
        hideRelated
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              className={cn(
                "gap-1.5 border font-normal",
                kimiOn
                  ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-100"
                  : "border-amber-400/30 bg-amber-500/10 text-amber-100",
              )}
            >
              <Zap className="h-3 w-3" />
              {kimiOn ? "Motor ON" : "Motor OFF"}
            </Badge>
            <Badge
              className={cn(
                "gap-1.5 border font-normal",
                llmKeyOk
                  ? "border-violet-400/30 bg-violet-500/10 text-violet-100"
                  : "border-white/15 bg-white/5 text-white/50",
              )}
            >
              <Brain className="h-3 w-3" />
              {llmKeyOk ? liveConfig?.llm_model || "LLM" : "Sin LLM key"}
            </Badge>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => {
                void loadLive();
                void load();
              }}
              disabled={liveLoading || loading}
            >
              {liveLoading || loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Live toolbar — never inside the title row */}
      <div className="flex flex-col gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
            Ventana live
          </span>
          <div className="flex items-center gap-0.5 rounded-lg border border-white/10 bg-black/30 p-0.5">
            {LIVE_HOURS_OPTIONS.map((h) => (
              <Button
                key={h}
                type="button"
                size="sm"
                variant={liveHours === h ? "default" : "ghost"}
                className={cn(
                  "h-8 px-3 text-xs",
                  liveHours === h && "bg-violet-600 text-white hover:bg-violet-500",
                )}
                onClick={() => setLiveHours(h)}
              >
                {h}h
              </Button>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5 border-white/10"
            onClick={() => void loadLive()}
            disabled={liveLoading && !live}
          >
            {liveLoading || liveSilent ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Activity className="h-3.5 w-3.5" />
            )}
            Live
          </Button>
          {liveUpdatedLabel ? (
            <span className="text-[11px] text-white/35">{liveUpdatedLabel}</span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
            Veredicto
          </span>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value || todayUtc())}
            className="h-9 w-[150px] border-white/10 bg-black/30"
            aria-label="Fecha veredicto (UTC)"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-white/10"
            onClick={() => setDate(todayUtc())}
            disabled={date === todayUtc()}
          >
            Hoy UTC
          </Button>
          <Button
            type="button"
            size="sm"
            className="gap-1.5"
            onClick={() => void runAggregate()}
            disabled={aggregating || loading || llmRunning}
          >
            {aggregating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Aggregate
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="gap-1.5"
            onClick={() => void runLlmInsight()}
            disabled={llmRunning || loading || aggregating}
          >
            {llmRunning ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Brain className="h-3.5 w-3.5" />
            )}
            Insight LLM
          </Button>
        </div>
      </div>

      {/* Runtime pulse KPIs */}
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
        {[
          {
            label: "Resolves",
            value: resolvesN,
            hint: "kimi_decisions",
            icon: Activity,
            tone: "text-sky-300",
          },
          {
            label: "Treatment",
            value: liveMetrics?.treatment ?? 0,
            hint: "cupón ANA*",
            icon: Tag,
            tone: "text-fuchsia-300",
          },
          {
            label: "Holdout",
            value: liveMetrics?.holdout ?? 0,
            hint: `${liveConfig?.holdout_pct ?? 15}% control`,
            icon: Zap,
            tone: "text-amber-300",
          },
          {
            label: "Checkout open",
            value: openN,
            hint: "stamp Stripe",
            icon: CheckCircle2,
            tone: "text-violet-300",
          },
          {
            label: "Paid",
            value: paidN,
            hint: `LLM ×${liveMetrics?.with_llm ?? 0}`,
            icon: Sparkles,
            tone: "text-emerald-300",
          },
        ].map(({ label, value, hint, icon: Icon, tone }) => (
          <div
            key={label}
            className="flex items-start gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.03] px-4 py-3"
          >
            <div className="mt-0.5 rounded-md border border-white/10 bg-white/[0.04] p-2">
              <Icon className={cn("h-4 w-4", tone)} />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-white/40">
                {label}
              </p>
              <p className="text-2xl font-semibold tabular-nums text-white/90">
                {liveLoading && !live ? "…" : value.toLocaleString("es-AR")}
              </p>
              <p className="mt-0.5 text-[10px] text-white/40">{hint}</p>
            </div>
          </div>
        ))}
      </div>

      {(liveWarnings.length > 0 ||
        live?.brand_source_key ||
        liveConfig?.offer_llm) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-white/45">
          {live?.brand_source_key && (
            <Badge variant="outline" className="border-white/15 font-mono text-[10px]">
              {live.brand_source_key}
            </Badge>
          )}
          <Badge variant="outline" className="border-white/15 font-mono text-[10px]">
            live {liveHours}h
          </Badge>
          {liveConfig?.offer_llm && (
            <>
              <Badge className="border-violet-400/30 bg-violet-500/10 font-mono text-[10px] font-normal text-violet-100">
                LLM primary={liveConfig.offer_llm.primary || "—"}
              </Badge>
              {liveConfig.offer_llm.fallback ? (
                <Badge className="border-sky-400/25 bg-sky-500/10 font-mono text-[10px] font-normal text-sky-100">
                  fallback={liveConfig.offer_llm.fallback}
                </Badge>
              ) : null}
              {liveConfig.offer_llm.reinforce ? (
                <Badge className="border-emerald-400/25 bg-emerald-500/10 font-mono text-[10px] font-normal text-emerald-100">
                  reinforce={liveConfig.offer_llm.reinforce}
                </Badge>
              ) : null}
            </>
          )}
          {(liveMetrics?.by_llm_provider || []).slice(0, 4).map((row) => (
            <Badge
              key={`prov-${row.key}`}
              variant="outline"
              className="border-white/15 font-mono text-[10px] text-white/60"
            >
              {row.key}×{row.count}
            </Badge>
          ))}
          {liveWarnings.map((w) => (
            <Badge
              key={w}
              className="border-amber-400/25 bg-amber-500/10 font-normal text-amber-100/90"
            >
              {w}
            </Badge>
          ))}
        </div>
      )}

      {/* Stripe brand paid — revenue coherence vs kimi outcomes */}
      {liveMetrics?.stripe_paid != null && (
        <section className="space-y-2 rounded-2xl border border-emerald-400/20 bg-emerald-500/[0.05] px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-200/80">
                Stripe paid (brand)
              </p>
              <p className="text-xs text-white/50">
                Checkout Sessions con metadata.source = brand · no depende de
                outcomes Kimi.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                className={cn(
                  "font-mono text-[10px]",
                  liveMetrics.stripe_paid.ok
                    ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-100"
                    : "border-amber-400/30 bg-amber-500/10 text-amber-100",
                )}
              >
                {liveMetrics.stripe_paid.ok ? "ok" : "err"} ·{" "}
                {liveMetrics.stripe_paid.buyers_count ?? 0} · $
                {(
                  (liveMetrics.stripe_paid.total_amount_cents ?? 0) / 100
                ).toFixed(2)}
              </Badge>
              <a
                href="/admin/buyers?days=2"
                className="text-[11px] font-medium text-emerald-200/90 underline-offset-2 hover:underline"
              >
                Ver compradores →
              </a>
            </div>
          </div>
          {!liveMetrics.stripe_paid.ok && liveMetrics.stripe_paid.error ? (
            <p className="text-[11px] text-amber-100/80">
              {liveMetrics.stripe_paid.error}
            </p>
          ) : null}
          {(liveMetrics.stripe_paid.buyers?.length ?? 0) > 0 ? (
            <ul className="space-y-1 text-[11px] text-white/60">
              {(liveMetrics.stripe_paid.buyers || []).slice(0, 3).map((b, i) => {
                const amt =
                  typeof b?.amount === "number"
                    ? `$${((b.amount ?? 0) / 100).toFixed(2)}`
                    : "—";
                const cur = (b?.currency || "usd").toUpperCase();
                const cc = b?.country || "??";
                return (
                  <li
                    key={`${b?.email || "x"}-${i}`}
                    className="flex flex-wrap gap-x-2 font-mono"
                  >
                    <span className="text-white/80">{b?.email || "—"}</span>
                    <span className="text-white/40">
                      {amt} {cur}
                    </span>
                    <span className="text-white/35">{cc}</span>
                  </li>
                );
              })}
            </ul>
          ) : liveMetrics.stripe_paid.ok ? (
            <p className="text-[11px] text-white/40">
              Sin pagos brand en la ventana Stripe (días ≈ hours/24).
            </p>
          ) : null}
        </section>
      )}

      {/* Validation checklist — DB + PH event correlation (closes rrweb gaps) */}
      {liveMetrics?.validation && (
        <section className="space-y-3 rounded-2xl border border-sky-400/20 bg-sky-500/[0.05] px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-200/80">
                Validación pipeline (DB)
              </p>
              <p className="text-xs text-white/50">
                Checklist real de decisiones/outcomes — no dependas del export
                rrweb (no trae body de red ni eventos PH).
              </p>
            </div>
            <Badge className="border-sky-400/30 bg-sky-500/10 font-mono text-[10px] text-sky-100">
              loop {liveMetrics.validation.loop_complete_pct ?? 0}% paid
            </Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["treatment", liveMetrics.validation.has_treatment],
                ["holdout", liveMetrics.validation.has_holdout],
                ["checkout_open", liveMetrics.validation.has_checkout_open],
                ["paid", liveMetrics.validation.has_paid],
                ["abandoned", liveMetrics.validation.has_abandoned],
              ] as const
            ).map(([label, ok]) => (
              <Badge
                key={label}
                className={cn(
                  "font-mono text-[10px]",
                  ok
                    ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-100"
                    : "border-white/10 bg-white/5 text-white/40",
                )}
              >
                {ok ? "✓" : "·"} {label}
              </Badge>
            ))}
            {(liveMetrics.validation.sample_codes || []).map((code) => (
              <Badge
                key={code}
                className="border-fuchsia-400/30 bg-fuchsia-500/10 font-mono text-[10px] text-fuchsia-100"
              >
                {code}
              </Badge>
            ))}
          </div>
          <ul className="space-y-1 text-[11px] leading-relaxed text-white/55">
            {(liveMetrics.validation.notes || []).map((line) => (
              <li key={line} className="flex gap-1.5">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-sky-300/70" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Live React Flow canvas */}
      <KimiCanvasErrorBoundary>
        <Suspense
          fallback={
            <div className="flex h-[420px] items-center justify-center rounded-2xl border border-violet-400/20 bg-[#070708] text-xs text-white/40">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Cargando canvas live…
            </div>
          }
        >
          <LazyKimiLiveFlowCanvas
            config={liveConfig}
            metrics={liveMetrics}
            warnings={liveWarnings}
            updatedLabel={liveUpdatedLabel}
          />
        </Suspense>
      </KimiCanvasErrorBoundary>

      {/* Recent decisions stream */}
      <section className="space-y-3 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
              Stream live · DualCTA
            </p>
            <h2 className="text-sm font-medium text-white/90">
              1 fila = 1 decisión de cupón
            </h2>
            <p className="mt-0.5 text-xs text-white/40">
              Outcomes: resolved → checkout_opened → paid / abandoned · auto{" "}
              {LIVE_POLL_MS / 1000}s
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          {liveLoading && recentDecisions.length === 0 ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-white/40" />
            </div>
          ) : recentDecisions.length === 0 ? (
            <p className="py-6 text-center text-sm text-white/40">
              Sin decisiones en esta ventana. Ampliá a 24h o esperá tráfico INT.
            </p>
          ) : (
            <table className="w-full min-w-[1080px] text-left text-xs">
              <thead>
                <tr className="border-b border-white/[0.07] text-[11px] uppercase tracking-wider text-white/35">
                  <th className="pb-2 pr-2 font-medium">hora</th>
                  <th className="pb-2 pr-2 font-medium">geo</th>
                  <th className="pb-2 pr-2 font-medium">bucket</th>
                  <th className="pb-2 pr-2 font-medium">código</th>
                  <th className="pb-2 pr-2 font-medium">%</th>
                  <th className="pb-2 pr-2 font-medium">LLM</th>
                  <th className="pb-2 pr-2 font-medium">shadow</th>
                  <th className="pb-2 pr-2 font-medium">source</th>
                  <th className="pb-2 pr-2 font-medium">pattern</th>
                  <th className="pb-2 font-medium">outcomes</th>
                </tr>
              </thead>
              <tbody>
                {recentDecisions.map((row, i) => {
                  const outcomes = Array.isArray(row.outcomes)
                    ? (row.outcomes as string[]).join(" → ")
                    : "—";
                  const paidRow =
                    Array.isArray(row.outcomes) &&
                    (row.outcomes as string[]).includes("paid");
                  const llmProv = row.llm_provider
                    ? String(row.llm_provider)
                    : null;
                  const shadowProv = row.llm_shadow_provider
                    ? String(row.llm_shadow_provider)
                    : null;
                  const shadowAgrees = row.llm_shadow_agrees;
                  return (
                    <tr
                      key={String(row.id ?? i)}
                      className="border-b border-white/[0.05] align-top text-white/80"
                    >
                      <td className="py-2.5 pr-2 tabular-nums text-white/45">
                        {formatTs(row.created_at)}
                      </td>
                      <td className="py-2.5 pr-2">
                        <span className="font-mono">
                          {String(row.country ?? "??")}
                        </span>
                        {row.city ? (
                          <span className="ml-1 text-white/40">
                            {String(row.city)}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2.5 pr-2">
                        <Badge
                          className={cn(
                            "text-[10px] font-mono font-normal",
                            row.kimi_bucket === "treatment"
                              ? "border-fuchsia-400/30 bg-fuchsia-500/10 text-fuchsia-100"
                              : "border-white/15 bg-white/5 text-white/60",
                          )}
                        >
                          {String(row.kimi_bucket ?? "—")}
                        </Badge>
                      </td>
                      <td className="py-2.5 pr-2 font-mono text-[11px] text-violet-100/90">
                        {String(row.promotion_code ?? "—")}
                      </td>
                      <td className="py-2.5 pr-2 tabular-nums">
                        {row.percent_off != null ? `${row.percent_off}%` : "—"}
                      </td>
                      <td className="py-2.5 pr-2">
                        {llmProv ? (
                          <div className="space-y-0.5">
                            <Badge
                              className={cn(
                                "text-[10px] font-mono font-normal",
                                llmProv === "deepseek"
                                  ? "border-sky-400/30 bg-sky-500/10 text-sky-100"
                                  : "border-violet-400/30 bg-violet-500/10 text-violet-100",
                              )}
                            >
                              {llmProv}
                              {row.llm_used_fallback ? "·fb" : ""}
                            </Badge>
                            {row.llm_latency_ms != null ? (
                              <p className="font-mono text-[9px] text-white/35">
                                {String(row.llm_latency_ms)}ms
                                {row.llm_ok === false ? " · fail" : ""}
                              </p>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-white/30">—</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-2">
                        {shadowProv ? (
                          <div className="space-y-0.5">
                            <Badge
                              className={cn(
                                "text-[10px] font-mono font-normal",
                                shadowAgrees === true ||
                                  shadowAgrees === "true"
                                  ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-100"
                                  : shadowAgrees === false ||
                                      shadowAgrees === "false"
                                    ? "border-amber-400/30 bg-amber-500/10 text-amber-100"
                                    : "border-white/15 bg-white/5 text-white/60",
                              )}
                            >
                              {shadowProv}
                              {row.llm_shadow_tier
                                ? `·${String(row.llm_shadow_tier)}`
                                : ""}
                            </Badge>
                            <p className="font-mono text-[9px] text-white/35">
                              {shadowAgrees === true || shadowAgrees === "true"
                                ? "agree"
                                : shadowAgrees === false ||
                                    shadowAgrees === "false"
                                  ? "disagree"
                                  : row.llm_shadow_ok === "false" ||
                                      row.llm_shadow_ok === false
                                    ? "skip/fail"
                                    : "—"}
                            </p>
                          </div>
                        ) : (
                          <span className="text-white/30">—</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-2 font-mono text-[10px] text-white/40">
                        {String(row.source ?? "—")}
                      </td>
                      <td className="py-2.5 pr-2 text-[10px] text-white/40">
                        {String(row.pattern_label ?? "—")}
                      </td>
                      <td
                        className={
                          paidRow
                            ? "py-2.5 font-medium text-emerald-300"
                            : "py-2.5 text-white/45"
                        }
                      >
                        {outcomes}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Daily verdict */}
      <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.06] pt-5 text-xs text-white/45">
        <p className="mr-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
          Veredicto diario
        </p>
        <Badge variant="outline" className="border-white/15 font-mono text-[10px]">
          {data?.verdict_date || date}
        </Badge>
        {data?.market && <span>mercado: {data.market}</span>}
        {data?.brand_source_key && (
          <span className="font-mono">· {data.brand_source_key}</span>
        )}
        {data?.llm && (
          <Badge
            className={cn(
              "font-mono text-[10px]",
              data.llm.key_configured
                ? "border-violet-400/30 bg-violet-500/10 text-violet-100"
                : "border-red-400/30 bg-red-500/10 text-red-100",
            )}
          >
            LLM {data.llm.key_configured ? "key OK" : "sin key"} ·{" "}
            {data.llm.model || "?"}
          </Badge>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        {[
          { label: "Decisiones (día)", value: stats?.decisions ?? 0 },
          { label: "Con promo", value: stats?.with_promo ?? 0 },
          { label: "Códigos distintos", value: stats?.distinct_codes ?? 0 },
        ].map(({ label, value }) => (
          <div
            key={label}
            className="rounded-2xl border border-white/[0.07] bg-white/[0.03] px-4 py-3"
          >
            <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/40">
              <Tag className="h-3.5 w-3.5" />
              {label}
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-white/90">
              {loading && !data ? "…" : value}
            </p>
          </div>
        ))}
      </div>

      <Card className="border-white/[0.07] bg-white/[0.02]">
        <CardHeader>
          <CardTitle className="text-base text-white/90">Patterns del día</CardTitle>
          <CardDescription className="text-white/40">
            Recomendaciones de cupón por segmento · {data?.verdict_date || date} (UTC)
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-3 font-medium">pattern_key</th>
                <th className="pb-2 pr-3 font-medium">status</th>
                <th className="pb-2 pr-3 font-medium text-right">conf.</th>
                <th className="pb-2 pr-3 font-medium text-right">n</th>
                <th className="pb-2 font-medium">recomendación</th>
              </tr>
            </thead>
            <tbody>
              {loading && patterns.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              )}
              {!loading && patterns.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-muted-foreground">
                    Sin patterns para este día. Corré aggregate si hay decisiones.
                  </td>
                </tr>
              )}
              {patterns.map((p) => (
                <tr key={p.id} className="border-b border-border/40 align-top">
                  <td className="py-2.5 pr-3">
                    <code className="text-xs font-mono">{p.pattern_key}</code>
                    {p.title && (
                      <div className="mt-0.5 text-xs text-muted-foreground">{p.title}</div>
                    )}
                  </td>
                  <td className="py-2.5 pr-3">
                    <Badge variant={statusBadgeVariant(p.status)} className="text-xs">
                      {p.status}
                    </Badge>
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">
                    {Number(p.confidence ?? 0).toFixed(2)}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums">
                    {p.sample_size ?? 0}
                  </td>
                  <td className="py-2.5 text-xs text-muted-foreground">
                    {formatRecommendation(p.recommendation)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Oportunidades abiertas
            {openOpportunities.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {openOpportunities.length}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Cambios de política propuestos para el día. Accept marca status = accepted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && openOpportunities.length === 0 && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && openOpportunities.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No hay oportunidades open para este día.
            </p>
          )}
          {openOpportunities.map((op) => (
            <div
              key={op.id}
              className="flex flex-col gap-3 rounded-lg border border-border/60 p-4 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium">{op.title || "Sin título"}</p>
                  {op.kind && (
                    <Badge variant="outline" className="text-xs font-mono">
                      {op.kind}
                    </Badge>
                  )}
                  {typeof op.priority === "number" && (
                    <span className="text-xs text-muted-foreground">
                      prio {op.priority}
                    </span>
                  )}
                </div>
                {op.description && (
                  <p className="text-sm text-muted-foreground">{op.description}</p>
                )}
                <p className="font-mono text-[10px] text-muted-foreground/70">{op.id}</p>
              </div>
              <Button
                size="sm"
                className="shrink-0"
                disabled={acceptingId === op.id}
                onClick={() => void acceptOpportunity(op.id)}
              >
                {acceptingId === op.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                <span className="ml-2">Aceptar</span>
              </Button>
            </div>
          ))}

          {otherOpportunities.length > 0 && (
            <div className="border-t border-border/40 pt-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                Otras ({otherOpportunities.length})
              </p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {otherOpportunities.map((op) => (
                  <li key={op.id} className="flex flex-wrap items-center gap-2">
                    <Badge variant={statusBadgeVariant(op.status)} className="text-xs">
                      {op.status}
                    </Badge>
                    <span>{op.title || op.id}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminPageShell>
  );
}
